/**
 * FR-9's heat-table legend: the two rank scales, the two cell treatments, and the one line of
 * guidance the current view needs.
 *
 * Always present — "Organic (Org)", "Sponsored (Spn)", "No data" and "Ranked via variant ASIN" —
 * plus exactly one extra note driven by the rank type: Organic view says the table shows organic
 * ranks only; Sponsored view says the roll-up rows stay organic. Both view adds nothing, because
 * both scales are on screen and both are explained above.
 *
 * A pure function of its one prop: no fetching, no storage, no DOM reads. The swatches name the
 * heat tokens directly (`hsl(var(--heat-rank-N))` / `hsl(var(--heat-sp-rank-N))`) rather than going
 * through `getRankHeat`, so the legend has no `getComputedStyle` dependency and no second copy of
 * the ramp's colours to drift from `tokens.css`.
 *
 * **Why six swatches and not the captured design's seven labelled bands:** the prototype legend
 * documents its seven-step *discrete* scale, which `frontend-new` does not have — rank is painted on
 * a six-stop logarithmic ramp (`decisions/the-rank-heat-scale-is-six-stops-on-a-log-ramp.md`, and
 * for sponsored `decisions/sponsored-rank-shares-the-organic-log-ramp-in-indigo.md`, intent FR-8).
 * A legend showing seven bands would describe a scale this table does not use, so each scale is
 * shown as its six anchor ranks instead.
 */

import { SwatchBook } from "lucide-react";

import type { RankType } from "./RankTypeToggle";
import { NO_READING_LABEL } from "./constants";

/** The ramp's six anchor ranks, in ascending order, as `heatScale.ts` anchors them. */
const RAMP_ANCHORS: ReadonlyArray<{ rank: number; label: string }> = [
  { rank: 1, label: "1" },
  { rank: 10, label: "10" },
  { rank: 25, label: "25" },
  { rank: 50, label: "50" },
  { rank: 100, label: "100" },
  { rank: 200, label: "200+" },
];

/** The one extra line each view carries. Both view carries none. */
const VIEW_NOTES: Record<RankType, string> = {
  organic: "Organic ranks only — position among organic results, excluding sponsored",
  sponsored: "Product and theme rows stay organic (dimmed) in Sponsored view",
  both: "",
};

interface RampScaleProps {
  label: string;
  /** `--heat-rank` or `--heat-sp-rank` — the family whose six anchors this scale samples. */
  tokenPrefix: "--heat-rank" | "--heat-sp-rank";
  testId: string;
}

function RampScale({ label, tokenPrefix, testId }: RampScaleProps) {
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <span className="text-xs font-semibold text-foreground">{label}</span>
      <div className="flex items-end gap-1">
        {RAMP_ANCHORS.map((anchor) => (
          <div key={anchor.rank} className="flex flex-col items-center gap-0.5">
            <span
              aria-hidden="true"
              className="h-3 w-7 rounded-[2px]"
              style={{ backgroundColor: `hsl(var(${tokenPrefix}-${anchor.rank}))` }}
            />
            <span className="text-[10px] leading-none text-muted-foreground tabular-nums">
              {anchor.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export interface RankLegendProps {
  /** The current view — `RankHeatmapTable/index.tsx` threads it from `useRankTypePreference`. */
  rankType: RankType;
}

export function RankLegend({ rankType }: RankLegendProps) {
  const note = VIEW_NOTES[rankType];

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="rank-legend">
      <RampScale label="Organic (Org)" tokenPrefix="--heat-rank" testId="rank-legend-scale-organic" />
      <RampScale
        label="Sponsored (Spn)"
        tokenPrefix="--heat-sp-rank"
        testId="rank-legend-scale-sponsored"
      />
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-foreground">No data</span>
        <span className="rounded-[2px] bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {NO_READING_LABEL}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-foreground">Ranked via variant ASIN</span>
        {/* A sample of the marked cell itself: the rank, carrying the variant marker. The marker is
            `WEB-004`'s `RankVariantBadge` on a real cell; here it is one icon with no tooltip. The
            glyph is the shared `SwatchBook` call-out (AUT-443 `SiblingRankBadge` / `RankVariantBadge`),
            so the legend and both the organic and sponsored cells show one consistent marker (AUT-520). */}
        <span className="inline-flex items-center gap-0.5 rounded-[2px] bg-muted px-2 py-0.5 text-xs tabular-nums text-foreground">
          12
          <SwatchBook aria-hidden="true" className="h-3 w-3" strokeWidth={2} />
        </span>
      </div>
      {note !== "" && <span className="ml-auto text-xs text-muted-foreground">{note}</span>}
    </div>
  );
}
